import type {
  KambiCouponUnauthorizedErrorResponse,
  KambiCouponValidateSuccessResponse,
  KambiCouponPlaceSuccessResponse,
} from '@mobi/api-types/kambi/player/coupon'
import { post } from '@classic/Foundation/Services/ApiService'
import { store } from '@core/Store'
import { selectPlayerSessionId } from '@core/Areas/KambiClient/Store/selectors'
import type { KambiBettingParams, KambiPlaceBetBettingParams } from './types'
import { getWapi } from '@core/Areas/KambiClient/helpers'
import { startListening } from '@core/Store/listenerMiddleware'
import { updateUserData } from '@core/Areas/KambiClient/Store'

export const postValidateKambiSportsBet = ({ couponRows, bets }: KambiBettingParams) => {
  return postKambiBetToMobiApi<KambiBettingParams, KambiCouponValidateSuccessResponse>(
    '/api/kambibetting/validate-bet',
    { couponRows, bets }
  )
}

export const postPlaceKambiSportsBet = ({
  couponRows,
  bets,
  rewards,
  copiedBetLoopPostId,
}: KambiBettingParams) => {
  return postKambiBetToMobiApi<KambiPlaceBetBettingParams, KambiCouponPlaceSuccessResponse>(
    '/api/kambibetting/place-bet',
    {
      requestId: createRequestId(),
      couponRows,
      bets,
      rewards,
      copiedBetLoopPostId,
    }
  )
}

// =============
// Local Helpers
// =============

async function postKambiBetToMobiApi<TRequest, TResponse>(
  url: string,
  body: TRequest
): Promise<TResponse> {
  let kambiBearerToken = selectPlayerSessionId(store.getState())

  if (!kambiBearerToken) {
    kambiBearerToken = await getKambiPlayerToken()
  }

  if (!kambiBearerToken) {
    const unauthorizedKambiTokenError: KambiCouponUnauthorizedErrorResponse = {
      status: 401,
      message: 'Unauthorized',
    }
    throw unauthorizedKambiTokenError
  }

  const additionalHeaders = { ['X-Kambi-Bearer']: kambiBearerToken }
  return post<TResponse>({ url, body, additionalHeaders })
}

let sessionCommitCount = 0
function createRequestId(): string {
  return `${Date.now()}-${++sessionCommitCount}`
}

const getKambiPlayerToken = () =>
  new Promise<string>(resolve => {
    getWapi().then(wapi => {
      const dispose = startListening({
        actionCreator: updateUserData,
        effect: action => {
          if (action.payload) {
            resolve(action.payload)
            dispose()
          }
        },
      })
      wapi.request(wapi.USER_DATA)
    })
  })

// cspell:words Wapi kambibetting
